import { generateCaregilityURL } from "./utils/aesEncryptor";
import { generateObservationURL } from "./utils/aesEncryptor2";
import { FaVideo } from "react-icons/fa";
import { FaEye } from "react-icons/fa";
import React, { useEffect, useState } from "react";
import {
  collection,
  onSnapshot,
  addDoc,
  doc,
  updateDoc,
  arrayUnion,
  serverTimestamp,
  getDocs,
  orderBy,
  query,
} from "firebase/firestore";
import { db } from "./firebase";
import { getAuth } from "firebase/auth";
import { FaTrashAlt, FaCheckCircle, FaEdit, FaSave } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import "./TaskList.css";
import logo2 from "./logo2.png";

const TaskList = () => {
  const [tasks, setTasks] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [viewMode, setViewMode] = useState("list"); // Added for view toggle
  const navigate = useNavigate();
  const auth = getAuth();
  const [users, setUsers] = useState([]);
  const [newTask, setNewTask] = useState({
    text: "",
    status: "Pending",
    assignee: "",
    date: "",
    patientRoom: "",
  });
  const [error, setError] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editedStatus, setEditedStatus] = useState("Pending");
  const [activeVideoCardIds, setActiveVideoCardIds] = useState([]); 

  const saveStatus = async (taskId) => {
    try {
      await updateDoc(doc(db, "tasks", taskId), { status: editedStatus });

      setTasks((prevTasks) =>
        prevTasks.map((task) =>
          task.id === taskId ? { ...task, status: editedStatus } : task
        )
      );

      setEditingTaskId(null);
      setEditedStatus("");

      const snapshot = await getDocs(collection(db, "tasks"));
      const tasksData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      if (JSON.stringify(tasksData) !== JSON.stringify(tasks)) {
        setTasks(tasksData);
      }
    } catch (error) {
      console.error("Error updating status:", error);
      setError("Error updating status. Please try again.");
    }
  };

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "tasks"),
      (snapshot) => {
        const tasksData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        if (JSON.stringify(tasksData) !== JSON.stringify(tasks)) {
          setTasks(tasksData);
        }
      },
      (err) => {
        setError("Error fetching tasks. Please try again later.");
        console.error("Error fetching tasks:", err);
      }
    );

    const fetchUsers = async () => {
      const querySnapshot = await getDocs(collection(db, "users"));
      const usersData = querySnapshot.docs.map((doc) => doc.data());
      setUsers(usersData);
    };

    fetchUsers();

    return () => unsubscribe();
  }, [tasks]);

  const addTask = async () => {
    const currentUser = auth.currentUser;
    if (newTask.text.trim() && currentUser) {
      await addDoc(collection(db, "tasks"), {
        ...newTask,
        currentStatus: newTask.status,
        statusTimestamps: {
          pendingAt: serverTimestamp(),
        },
        statusHistory: [
          {
            status: "Pending",
            timestamp: new Date().toISOString(),
            updatedBy: currentUser.email,
          },
        ],
      })
        .then(() =>
          setNewTask({
            text: "",
            status: "Pending",
            assignee: "",
            date: "",
            currentStatus: "",
            patientRoom: "",
          })
        )
        .catch(() => setError("Error adding task. Please try again."));
    } else {
      setError("Task cannot be empty, and you must be logged in.");
    }
  };

  const dismissTask = async (taskId) => {
    try {
      const taskRef = doc(db, "tasks", taskId);
      await updateDoc(taskRef, {
        currentStatus: "Dismissed",
      });
      console.log("Task dismissed successfully!");
    } catch (error) {
      console.error("Error dismissing task:", error);
      setError("Error dismissing task. Please try again.");
    }
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      alert("No user is logged in!");
      return;
    }

    const taskRef = doc(db, "tasks", taskId);

    const statusTimestamps = {
      Pending: "pendingAt",
      "In Progress": "inProgressAt",
      "On Hold": "onHoldAt",
      Completed: "completedAt",
    };

    const timestampField = statusTimestamps[newStatus];

    try {
      await updateDoc(taskRef, {
        [timestampField]: serverTimestamp(),
        currentStatus: newStatus,
        statusHistory: arrayUnion({
          status: newStatus,
          timestamp: new Date().toISOString(),
          updatedBy: currentUser.email,
        }),
      });

      setTasks((prevTasks) =>
        prevTasks.map((task) =>
          task.id === taskId ? { ...task, status: newStatus, currentStatus: newStatus } : task
        )
      );

      setEditingTaskId(null);
    } catch (error) {
      console.error("Error updating task status:", error);
      setError("Error updating status. Please try again.");
    }
  };

  const filteredTasks = tasks.filter((task) =>
    filterStatus === "All"
      ? task.currentStatus !== "Dismissed"
      : task.currentStatus === filterStatus && task.currentStatus !== "Dismissed"
  );

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedTasks = [...filteredTasks].sort((a, b) => {
    const { key, direction } = sortConfig;
    if (!key) return 0;

    const aVal = a[key] || "";
    const bVal = b[key] || "";

    if (aVal < bVal) return direction === "asc" ? -1 : 1;
    if (aVal > bVal) return direction === "asc" ? 1 : -1;
    return 0;
  });

  const handleNavigateToReports = () => {
    navigate("/Reports");
  };

  const handleNavigateToVideoWall = () => {
    navigate("/video-wall");
  };

  const handleNavigateToPatientNavigator = () => {
    navigate("/patient-list");
  };

  const secretKey = "ae_YNM-srKs";

  const handleCameraClick = (deviceid) => {
    if (!deviceid) {
      alert("No device ID provided for this task.");
      return;
    }
    const url = generateCaregilityURL(deviceid, secretKey);
    window.open(url, "_blank");
  };

  const handleObservationClick = (taskId, deviceid) => {
    if (!deviceid) {
      alert("No device ID provided for this task.");
      return;
    }

    setActiveVideoCardIds((prev) =>
      prev.includes(taskId)
        ? prev.filter((id) => id !== taskId) // remove to close
        : [...prev, taskId] // add to open
    );
  };

  return (
    <div className="task-list-container">
      <img src={logo2} alt="Logo 2" className="task-logo" />
      <div className="task-header">
        <h1>Task List</h1>
        <div className="header-actions">
          <button onClick={handleNavigateToReports} className="navigate-button">
            Go To Reports
          </button>
          <button onClick={handleNavigateToVideoWall} className="navigate-button">
            Monitor Rooms
          </button>
          <button onClick={handleNavigateToPatientNavigator} className="navigate-button">
            Patient Census
          </button>

          {/* VIEW MODE TOGGLE */}
          <div className="view-toggle">
            <button
              className={viewMode === "list" ? "active" : ""}
              onClick={() => setViewMode("list")}
              title="List View"
            >
              List
            </button>
            <button
              className={viewMode === "card" ? "active" : ""}
              onClick={() => setViewMode("card")}
              title="Card View"
            >
              Cards
            </button>
          </div>

          <select onChange={(e) => setFilterStatus(e.target.value)} value={filterStatus}>
            <option value="All">All</option>
            <option value="In Progress">In Progress</option>
            <option value="On Hold">On Hold</option>
            <option value="Pending">Pending</option>
            <option value="Completed">Completed</option>
            <option value="Urgent">Urgent</option>
          </select>
        </div>
      </div>

      {error && <p className="error-message">{error}</p>}

      {viewMode === "list" ? (
        <table className="task-table">
          <thead>
            <tr>
              <th onClick={() => handleSort("currentStatus")}>
                Status {sortConfig.key === "currentStatus" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
              </th>
              <th onClick={() => handleSort("text")}>
                Task {sortConfig.key === "text" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
              </th>
              <th onClick={() => handleSort("assignee")}>
                Assignee {sortConfig.key === "assignee" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
              </th>
              <th onClick={() => handleSort("date")}>
                Date/Time {sortConfig.key === "date" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
              </th>
              <th onClick={() => handleSort("patientRoom")}>
                Patient/Room {sortConfig.key === "patientRoom" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
              </th>
              <th>Actions</th>
              <th>Call</th>
            </tr>
          </thead>
          <tbody>
            {sortedTasks.map((task) => (
              <tr
                key={task.id}
                className={`task-row ${task.currentStatus ? task.currentStatus.toLowerCase() : "pending"}`}
              >
                <td>
                  {editingTaskId === task.id ? (
                    <select
                      value={editedStatus}
                      onChange={(e) => setEditedStatus(e.target.value)}
                      onBlur={() => updateTaskStatus(task.id, editedStatus)}
                    >
                      <option value="Pending">Pending</option>
                      <option value="In Progress">In Progress</option>
                      <option value="On Hold">On Hold</option>
                      <option value="Completed">Completed</option>
                      <option value="Urgent">Urgent</option>
                    </select>
                  ) : (
                    <span
                      className={`status-indicator ${task.currentStatus
                        .toLowerCase()
                        .replace(/\s+/g, "-")}`}
                    >
                      {task.currentStatus}
                    </span>
                  )}
                </td>
                <td>{task.text}</td>
                <td>
                  <select
                    value={task.assignee || ""}
                    onChange={(e) =>
                      updateDoc(doc(db, "tasks", task.id), { assignee: e.target.value })
                    }
                  >
                    <option value="">Unassigned</option>
                    {users.map((user) => (
                      <option key={user.uid} value={user.email}>
                        {user.email}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  {task.date
                    ? new Date(task.date).toLocaleString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })
                    : "N/A"}
                </td>
                <td>{task.patientRoom || "N/A"}</td>
                <td>
                  {editingTaskId === task.id ? (
                    <button onClick={() => updateTaskStatus(task.id, editedStatus)}>
                      <FaSave />
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          setEditingTaskId(task.id);
                          setEditedStatus(task.currentStatus);
                        }}
                        title="Edit Task"
                      >
                        <FaEdit />
                      </button>
                      <button onClick={() => dismissTask(task.id)} title="Dismiss Task">
                        <FaTrashAlt />
                      </button>
                      <button onClick={() => updateTaskStatus(task.id, "Completed")} title="Mark Complete">
                        <FaCheckCircle />
                      </button>
                    </>
                  )}
                </td>
                <td>
                  <button onClick={() => handleCameraClick(task.deviceid)} title="Open Camera">
                    <FaVideo />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        // CARD VIEW
        <div className="card-view-container">
          {sortedTasks.map((task) => (
            <div key={task.id} className={`task-card ${task.currentStatus ? task.currentStatus.toLowerCase() : "pending"}`}>
              <div className="card-header">
                <h3>{task.text}</h3>
                <span className={`status-indicator ${task.currentStatus.toLowerCase().replace(/\s+/g, "-")}`}>
                  {task.currentStatus}
                </span>
              </div>
              <div className="card-details">
                <p><strong>Assignee:</strong> {task.assignee || "Unassigned"}</p>
                <p>
                  <strong>Date/Time:</strong>{" "}
                  {task.date
                    ? new Date(task.date).toLocaleString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })
                    : "N/A"}
                </p>
                <p><strong>Patient/Room:</strong> {task.patientRoom || "N/A"}</p>
              </div>
              <div className="card-actions">
                {editingTaskId === task.id ? (
                  <>
                    <button onClick={() => updateTaskStatus(task.id, editedStatus)} title="Save">
                      <FaSave />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => {
                      setEditingTaskId(task.id);
                      setEditedStatus(task.currentStatus);
                    }}
                    title="Edit Task"
                  >
                    <FaEdit />
                  </button>
                )}
                <button onClick={() => dismissTask(task.id)} title="Dismiss Task">
                  <FaTrashAlt />
                </button>
                <button onClick={() => updateTaskStatus(task.id, "Completed")} title="Mark Complete">
                  <FaCheckCircle />
                </button>
                <button onClick={() => handleObservationClick(task.id, task.deviceid)} title="Observe Room">
                  <FaEye />
                </button>
                <button onClick={() => handleCameraClick(task.deviceid)} title="Open Camera">
                  <FaVideo />
                </button>
              </div>
              
              {activeVideoCardIds.includes(task.id) && (
                <div className="video-tile mt-2 p-2 border rounded bg-gray-100">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-semibold">Live Observation Feed</span>
                    <button
                      onClick={() => setActiveVideoCardIds((prev) => prev.filter((id) => id !== task.id))}
                      className="text-xs text-red-500 hover:underline"
                    >
                      Close
                    </button>
                  </div>
                  <iframe
                    src={generateObservationURL(task.deviceid, secretKey)}
                    width="100%"
                    height="300"
                    allow="camera; microphone"
                    allowFullScreen
                    title={`Observation for ${task.id}`}
                    className="rounded shadow"
                  />
                </div>
              )}
               
              {/* Editable Fields */}
              {editingTaskId === task.id && (
                <div className="card-edit-form">
                  <label>Status:</label>
                  <select
                    value={editedStatus}
                    onChange={(e) => setEditedStatus(e.target.value)}
                  >
                    <option value="Pending">Pending</option>
                    <option value="In Progress">In Progress</option>
                    <option value="On Hold">On Hold</option>
                    <option value="Completed">Completed</option>
                    <option value="Urgent">Urgent</option>
                  </select>
                  <label>Assignee:</label>
                  <select
                    value={task.assignee || ""}
                    onChange={(e) =>
                      updateDoc(doc(db, "tasks", task.id), { assignee: e.target.value })
                    }
                  >
                    <option value="">Unassigned</option>
                    {users.map((user) => (
                      <option key={user.uid} value={user.email}>
                        {user.email}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add New Task Section */}
      <div className="add-task-section">
        <h2>Add New Task</h2>
        <input
          type="text"
          placeholder="Task description"
          value={newTask.text}
          onChange={(e) => setNewTask({ ...newTask, text: e.target.value })}
        />
        <select
          value={newTask.status}
          onChange={(e) => setNewTask({ ...newTask, status: e.target.value })}
        >
          <option value="Pending">Pending</option>
          <option value="In Progress">In Progress</option>
          <option value="On Hold">On Hold</option>
          <option value="Completed">Completed</option>
          <option value="Urgent">Urgent</option>
        </select>
        <select
          value={newTask.assignee}
          onChange={(e) => setNewTask({ ...newTask, assignee: e.target.value })}
        >
          <option value="">Assign to</option>
          {users.map((user) => (
            <option key={user.uid} value={user.email}>
              {user.email}
            </option>
          ))}
        </select>
        <input
          type="datetime-local"
          value={newTask.date}
          onChange={(e) => setNewTask({ ...newTask, date: e.target.value })}
        />
        <input
          type="text"
          placeholder="Patient/Room"
          value={newTask.patientRoom}
          onChange={(e) => setNewTask({ ...newTask, patientRoom: e.target.value })}
        />
        <button onClick={addTask}>Add Task</button>
      </div>
    </div>
  );
};

export default TaskList;